import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { PageOutro } from "@/components/site/PageEssentials";
import { Section, Breadcrumbs, CTABand } from "@/components/site/Blocks";
import { findTerm, glossary, type GlossaryTerm } from "@/data/glossary";
import { buildHead } from "@/lib/seo-head";

export const Route = createFileRoute("/glossary/$term")({
  loader: ({ params }) => {
    const t = findTerm(params.term);
    if (!t) throw notFound();
    return { term: t };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Term not found" }, { name: "robots", content: "noindex" }] };
    const t = loaderData.term;
    const title = `${t.term} — Definition & Investor Guide`;
    const url = `/glossary/${params.term}`;
    // t.short is a one-line definition (often <40 chars) — too thin for a SERP
    // snippet, so lead with it and back it with the investor-context sentence.
    const description = `${t.term} explained for real estate investors: ${t.short.replace(/\s+$/, "")}${
      /[.?!]$/.test(t.short.trim()) ? "" : "."
    } ${t.long}`;
    return buildHead({
      title,
      description,
      url,
      type: "article",
      keywords: `${t.term}, ${t.term} definition, ${t.category} glossary, ${t.term} real estate, ${t.term} investor loan term, ${t.term} meaning, DSCR loan glossary, mortgage underwriting terms`,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Glossary", url: "/glossary" },
        { name: t.term, url },
      ],
      service: { name: "Investor Loan Education & Terminology" },
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          name: t.term,
          description: t.long,
          inDefinedTermSet: "/glossary",
        },
      ],
    });
  },
  component: TermPage,
  notFoundComponent: () => (
    <SiteShell hideBreadcrumbs><Section><p className="py-16 text-center">Term not found. <Link to="/glossary" className="text-brand underline">Back to glossary</Link>.</p></Section></SiteShell>
  ),
});

function TermPage() {
  const { term: t } = Route.useLoaderData();
  const related: GlossaryTerm[] = (t.related ?? []).map((s: string) => glossary.find((x: GlossaryTerm) => x.slug === s)).filter((x: GlossaryTerm | undefined): x is GlossaryTerm => Boolean(x));
  return (
    <SiteShell hideBreadcrumbs>
      <PageHero eyebrow={t.category} title={t.term} subtitle={t.short} />
      <Section>
        <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Glossary", href: "/glossary" }, { name: t.term }]} />
        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <p className="text-lg text-muted-foreground">{t.long}</p>
            {t.example && (
              <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand">Example</div>
                <div className="mt-1 font-mono text-sm">{t.example}</div>
              </div>
            )}
            {related.length > 0 && (
              <div>
                <h3 className="font-semibold text-brand-dark">Related terms</h3>
                <ul className="mt-2 flex flex-wrap gap-2 text-sm">
                  {related.map((r: GlossaryTerm) => (
                    <li key={r.slug}>
                      <Link to="/glossary/$term" params={{ term: r.slug }} className="rounded-full border border-brand/30 px-3 py-1 text-brand hover:bg-brand hover:text-brand-foreground">
                        {r.term}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <aside className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-semibold text-brand-dark">Ready to run numbers?</h3>
            <p className="mt-2 text-sm text-muted-foreground">Get an initial scenario review from a licensed loan officer once we have your application and supporting docs. Formal pricing follows underwriting.</p>
            <Link to="/apply" className="mt-4 inline-block rounded-md bg-brand-gold px-4 py-2 text-sm font-semibold text-primary">Start My Approval</Link>
          </aside>
        </div>
      </Section>
      <CTABand title="Ready for real pricing?" body="Complete the short pre-qualification form and upload your supporting documents, and a licensed loan officer will follow up with an initial scenario review." />
      <PageOutro />
    </SiteShell>
  );
}
